import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AuthDialog({ open, onOpenChange, mode, setMode }) {
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [accountType, setAccountType] = useState("particular");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setEmail("");
    setPassword("");
    setName("");
    setPhone("");
    setAccountType("particular");
    setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let res;
    if (mode === "login") {
      res = await login(email, password);
    } else {
      res = await register({ email, password, name, phone: phone || null, account_type: accountType });
    }
    setLoading(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Sesión iniciada" : "Cuenta creada. ¡Bienvenido!");
      reset();
      onOpenChange(false);
    } else {
      setError(res.error || "Error inesperado");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auth-dialog"
        className="max-w-md rounded-none border border-[#E2E8F0] bg-[#F8FAFC] p-8"
      >
        <DialogHeader className="text-left space-y-1">
          <p className="eyebrow">
            {mode === "login" ? "Bienvenido de nuevo" : "Únete a la comunidad"}
          </p>
          <DialogTitle className="font-serif text-3xl font-semibold text-[#1A1A1A]">
            {mode === "login" ? "Inicia sesión" : "Crea tu cuenta"}
          </DialogTitle>
          <DialogDescription className="text-[#475569] text-sm">
            {mode === "login"
              ? "Accede para publicar y gestionar tus anuncios."
              : "Publica anuncios de viajes, pisos, apuntes y negocios en segundos."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 pt-2">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">
                Soy
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="account-type-particular"
                  onClick={() => setAccountType("particular")}
                  className={`h-11 border text-sm font-medium ${
                    accountType === "particular"
                      ? "bg-[#1A1A1A] text-[#F8FAFC] border-[#1A1A1A]"
                      : "border-[#CBD5E1] text-[#1A1A1A] bg-white hover:border-[#3B5FB8]"
                  }`}
                >
                  Particular
                </button>
                <button
                  type="button"
                  data-testid="account-type-empresa"
                  onClick={() => setAccountType("empresa")}
                  className={`h-11 border text-sm font-medium ${
                    accountType === "empresa"
                      ? "bg-[#1A1A1A] text-[#F8FAFC] border-[#1A1A1A]"
                      : "border-[#CBD5E1] text-[#1A1A1A] bg-white hover:border-[#3B5FB8]"
                  }`}
                >
                  Empresa
                </button>
              </div>
            </div>
          )}
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="auth-name" className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">
                {accountType === "empresa" ? "Nombre de la empresa" : "Nombre"}
              </Label>
              <Input
                id="auth-name"
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="rounded-none border-[#CBD5E1] h-11 bg-white"
                placeholder={accountType === "empresa" ? "Nombre comercial" : "Tu nombre"}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="auth-email" className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">
              Email
            </Label>
            <Input
              id="auth-email"
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-none border-[#CBD5E1] h-11 bg-white"
              placeholder="nombre@correo.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="auth-password" className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">
              Contraseña
            </Label>
            <Input
              id="auth-password"
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="rounded-none border-[#CBD5E1] h-11 bg-white"
              placeholder="Mínimo 6 caracteres"
            />
          </div>
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="auth-phone" className="text-xs font-mono uppercase tracking-[0.15em] text-[#475569]">
                Teléfono (opcional)
              </Label>
              <Input
                id="auth-phone"
                data-testid="auth-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded-none border-[#CBD5E1] h-11 bg-white"
                placeholder="+34 600 000 000"
              />
            </div>
          )}

          {error && (
            <p data-testid="auth-error" className="text-sm text-red-700 border border-red-200 bg-red-50 px-3 py-2">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid={mode === "login" ? "login-submit-button" : "register-submit-button"}
            disabled={loading}
            className="w-full bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] rounded-none h-12 font-medium tracking-wide"
          >
            {loading ? "Cargando..." : mode === "login" ? "Iniciar sesión" : "Crear cuenta"}
          </Button>

          <div className="pt-2 text-center text-sm text-[#475569]">
            {mode === "login" ? (
              <>
                ¿No tienes cuenta?{" "}
                <button
                  type="button"
                  data-testid="switch-to-register"
                  onClick={() => setMode("register")}
                  className="text-[#3B5FB8] font-semibold link-underline"
                >
                  Crear cuenta
                </button>
              </>
            ) : (
              <>
                ¿Ya tienes cuenta?{" "}
                <button
                  type="button"
                  data-testid="switch-to-login"
                  onClick={() => setMode("login")}
                  className="text-[#3B5FB8] font-semibold link-underline"
                >
                  Inicia sesión
                </button>
              </>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
